/**
 * @file IndividualSearchPage.js
 * @description This file contains the IndividualSearchPage component, which allows users to search for individuals based on various criteria.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { searchIndividuals } from '../../services/individualService';
import { impersonate } from '../../services/authService';
import {
    fetchPopulationGroups,
    fetchPopulationGroupIndividuals,
} from '../../services/populationGroupService';
import { SearchableDropdown } from '../helpers/SearchableDropdown_style_b_v2';
import { FilterModal, renderFilterIcon } from '../helpers/FilterComponent';
import styles from './IndividualPage.module.scss';
import Spinner from '../ui/spinners/Spinner';
import EmptyTableRow from '../ui/no_data_indicators/EmptyTableRow';
import PaginationComponent from '../helpers/PaginationComponent';
import { getStatusClass } from '../../utils/utils';
import {
    IndividualSearchSortField,
    SecurityAttributes,
    TenantUserStatusEnum,
    UserType,
} from '../../utils/data/enums';
import { useAuthContext } from '../../store/Contexts';
import debounce from 'lodash/debounce';
import { downloadReport } from './components/IndividualReport';
import Toast from '../ui/toasts/Toast';
import { useTableSort, SortDirections } from '../../hooks/useTableSort';
import { convertUTCDateTimeToLocalDateTime } from '../../components/helpers/DateUtils';
import { ROUTES } from '../../constants/routes';
import AudienceBadge from '../shared/AudienceBadge';
import { useAdminAudienceEnabled } from '../../hooks/useAdminAudienceEnabled';
import { getText } from '../../i18n';
const ITEMS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 1000;
const DEFAULT_SEARCH_PARAMS = {
    search: '',
    populationGroups: [],
    statuses: [],
    employmentCategories: [],
    jobTitles: [],
};
const COLUMN_FILTER_PARAMS = ['statuses', 'employmentCategories', 'jobTitles'];
const LIST_SEARCH_PARAMS = ['populationGroups', ...COLUMN_FILTER_PARAMS];
const SORT_FIELD_BY_COLUMN = {
    uniqueId: IndividualSearchSortField.UniqueId,
    lastName: IndividualSearchSortField.LastName,
    firstName: IndividualSearchSortField.FirstName,
    tenantUserStatus: IndividualSearchSortField.TenantUserStatus,
    employmentCategory: IndividualSearchSortField.EmploymentCategory,
    jobTitle: IndividualSearchSortField.JobTitle,
    lastLoginDate: IndividualSearchSortField.LastLoginDate,
};
const EMPTY_FILTER_OPTIONS = {
    statuses: [],
    employmentCategories: [],
    jobTitles: [],
};

function toFilterOptions(values, emptyLabel) {
    return [...new Set((values || []).map(value => value || emptyLabel))].sort(
        (a, b) => a.localeCompare(b),
    );
}

function buildFilterOptions({ statuses, employmentCategories, jobTitles }) {
    return {
        statuses: toFilterOptions(statuses, 'Not Started'),
        employmentCategories: toFilterOptions(employmentCategories, 'N/A'),
        jobTitles: toFilterOptions(jobTitles, 'N/A'),
    };
}

function buildListCriteria(searchParams) {
    return Object.fromEntries(
        LIST_SEARCH_PARAMS.filter(name => searchParams[name].length > 0).map(
            name => [name, searchParams[name]],
        ),
    );
}

function buildSearchCriteria(searchParams, sortConfig, page) {
    const search = searchParams.search.trim();

    return {
        page,
        pageSize: ITEMS_PER_PAGE,
        sortBy: SORT_FIELD_BY_COLUMN[sortConfig.key],
        sortDescending: sortConfig.direction === SortDirections.DESCENDING,
        ...(search && { search }),
        ...buildListCriteria(searchParams),
    };
}

function withDefaultTenantUserStatus(individual) {
    return {
        ...individual,
        tenantUserStatus: individual.tenantUserStatus || 'Not Started',
    };
}

const IndividualSearchPage = () => {
    const { user, hasAttribute, getFieldLabel } = useAuthContext();
    const { isAdminAudienceEnabled } = useAdminAudienceEnabled();
    const hasViewerDataFilter =
        isAdminAudienceEnabled && Boolean(user?.jwt?.df);

    const [searchParams, setSearchParams] = useState(DEFAULT_SEARCH_PARAMS);
    const [appliedSearchParams, setAppliedSearchParams] = useState(
        DEFAULT_SEARCH_PARAMS,
    );

    const [individuals, setIndividuals] = useState([]);
    const [filterOptions, setFilterOptions] = useState(EMPTY_FILTER_OPTIONS);
    const [populationGroups, setPopulationGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isPopulationGroupLoading, setIsPopulationGroupLoading] =
        useState(true);
    const navigate = useNavigate();
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);

    const { sortConfig, resetSortConfig, renderSortArrow } = useTableSort(
        'lastName',
        SortDirections.ASCENDING,
    );
    const [filterResetKey, setFilterResetKey] = useState(0);
    const [activeColumnFilter, setActiveColumnFilter] = useState(null);
    const canImpersonate =
        (hasAttribute(SecurityAttributes.ImpersonationClient) ||
            hasAttribute(SecurityAttributes.ImpersonationMultitenant)) &&
        user.jwt.impersonator == null;
    const columnCount = canImpersonate ? 8 : 7;

    const loadIndividuals = useCallback(
        async page => {
            setIsLoading(true);
            try {
                const results = await searchIndividuals(
                    buildSearchCriteria(appliedSearchParams, sortConfig, page),
                );
                setIndividuals(
                    (results.individuals || []).map(
                        withDefaultTenantUserStatus,
                    ),
                );
                setFilterOptions(buildFilterOptions(results));
                setTotalPages(
                    Math.ceil((results.recordCount || 0) / ITEMS_PER_PAGE),
                );
            } catch (error) {
                console.error('Error performing individual search:', error);
                setIndividuals([]);
                setTotalPages(0);
            } finally {
                setIsLoading(false);
            }
        },
        [appliedSearchParams, sortConfig],
    );

    useEffect(() => {
        setCurrentPage(1);
        loadIndividuals(1);
    }, [loadIndividuals]);

    const debouncedApplySearchParams = useMemo(
        () => debounce(setAppliedSearchParams, SEARCH_DEBOUNCE_MS),
        [],
    );

    useEffect(() => {
        debouncedApplySearchParams(searchParams);
    }, [searchParams, debouncedApplySearchParams]);

    useEffect(() => {
        return () => {
            debouncedApplySearchParams.cancel();
        };
    }, [debouncedApplySearchParams]);

    useEffect(() => {
        const loadPopulationGroups = async () => {
            try {
                setIsPopulationGroupLoading(true);
                const groups = await fetchPopulationGroups();
                setPopulationGroups(groups);
            } catch (error) {
                console.error('Could not fetch population groups', error);
            } finally {
                setIsPopulationGroupLoading(false);
            }
        };

        loadPopulationGroups();
    }, []);

    function handlePageChange(newPage) {
        setCurrentPage(newPage);
        loadIndividuals(newPage);
    }

    const clearFilters = () => {
        resetSortConfig();
        debouncedApplySearchParams.cancel();
        setSearchParams(DEFAULT_SEARCH_PARAMS);
        setAppliedSearchParams(DEFAULT_SEARCH_PARAMS);
        setFilterResetKey(prev => prev + 1);
    };

    const viewerAudienceLabel = useMemo(() => {
        if (!hasViewerDataFilter) {
            return '';
        }

        const matchingGroup = populationGroups.find(
            group => group.id === user?.jwt?.df,
        );

        return matchingGroup?.name || String(user?.jwt?.df || '');
    }, [hasViewerDataFilter, populationGroups, user?.jwt?.df]);

    const handleInputChange = e => {
        const { name, value } = e.target;
        setSearchParams(prev => ({ ...prev, [name]: value }));
    };

    function updateSearchParamList(name, selectedOptions) {
        const nextValues = selectedOptions
            .map(option => option?.id)
            .filter(Boolean);

        setSearchParams(prev => {
            if (prev[name].join('|') === nextValues.join('|')) {
                return prev;
            }
            return { ...prev, [name]: nextValues };
        });
    }

    function applyColumnFilter(name, values) {
        const nextSearchParams = { ...searchParams, [name]: values };
        setSearchParams(nextSearchParams);
        debouncedApplySearchParams.cancel();
        setAppliedSearchParams(nextSearchParams);
    }

    function openColumnFilter(event, name) {
        event.stopPropagation();
        setActiveColumnFilter(name);
    }

    function closeColumnFilter() {
        setActiveColumnFilter(null);
    }

    function renderColumnFilter(name) {
        return (
            <>
                <span
                    onClick={event => openColumnFilter(event, name)}
                    className={styles.cursorPointer}
                >
                    {renderFilterIcon(searchParams[name].length > 0)}
                </span>
                {activeColumnFilter === name && (
                    <FilterModal
                        column={name}
                        options={filterOptions[name]}
                        selectedOptions={searchParams[name]}
                        onApply={applyColumnFilter}
                        onClose={closeColumnFilter}
                    />
                )}
            </>
        );
    }

    const downloadIndividualReport = async () => {
        try {
            setIsLoading(true);
            downloadReport();
        } catch (error) {
            console.error('Error downloading report', error);
            Toast.error(getText('msg_821613944fc1'));
        } finally {
            setIsLoading(false);
        }
    };

    const getJwtPayload = token => {
        try {
            const payload = token.split('.')[1];
            const normalizedPayload = payload
                .replace(/-/g, '+')
                .replace(/_/g, '/');

            return JSON.parse(
                decodeURIComponent(
                    window
                        .atob(normalizedPayload)
                        .split('')
                        .map(
                            character =>
                                `%${character
                                    .charCodeAt(0)
                                    .toString(16)
                                    .padStart(2, '0')}`,
                        )
                        .join(''),
                ),
            );
        } catch (error) {
            console.error('Error decoding impersonation JWT payload:', error);
            return null;
        }
    };

    const uniqueIdLabel = getFieldLabel('Unique ID');

    const handleImpersonate = async impersonatedId => {
        try {
            const response = await impersonate(impersonatedId);

            if (response?.access_token) {
                const impersonatedJwt = getJwtPayload(response.access_token);
                const impersonatedUserType = impersonatedJwt?.type;

                const impersonatedUserIsAdmin =
                    impersonatedUserType !== undefined &&
                    impersonatedUserType <= UserType.ClientAdmin;

                window.localStorage.setItem(
                    'impersonation_token',
                    response.access_token,
                );

                window.localStorage.setItem(
                    'adminView',
                    String(impersonatedUserIsAdmin),
                );

                window.location.href = ROUTES.ROOT; // Navigate to root and reload
            } else {
                console.error('Impersonation token missing in response');
                Toast.error(getText('msg_6fff5a646e23'));
            }
        } catch (error) {
            console.error('Error during impersonation:', error);
            if (error?.response?.data?.detail) {
                Toast.error(error.response.data.detail);
            } else {
                Toast.error(getText('msg_0eb37f191eab'));
            }
        }
    };

    return (
        <div
            className="page-content"
            style={{ height: '100%', overflowY: 'auto' }}
        >
            <div className="card" style={{ minHeight: '100%' }}>
                <div
                    className="card-body px-0 py-4 d-flex flex-column"
                    style={{ minHeight: '100%' }}
                >
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div
                            className="float-start d-flex"
                            style={{ flexDirection: 'column' }}
                        >
                            <p className="page-title card-title ms-4 me-5">
                                {getText('msg_a96e829dca13')}
                            </p>
                            <p
                                className="ing-portals-subtitle mt-1"
                                style={{ marginTop: '10px' }}
                            >
                                {getText('msg_f30e1bff9059')}
                            </p>
                        </div>
                        <div className="float-end d-flex align-items-center">
                            {hasViewerDataFilter && (
                                <span className="mx-3">
                                    <AudienceBadge
                                        label={viewerAudienceLabel}
                                    />
                                </span>
                            )}
                            <span
                                type="button"
                                onClick={clearFilters}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-arrow-counterclockwise me-2" />
                                {getText('msg_381c4bf1d409')}
                            </span>

                            <span
                                type="button"
                                onClick={() => downloadIndividualReport()}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-cloud-download me-2" />
                                {getText('msg_a479c9c34e87')}
                            </span>
                            {hasAttribute(
                                SecurityAttributes.IndividualFullAccess,
                            ) && (
                                <span
                                    onClick={() =>
                                        navigate(ROUTES.INDIVIDUALS_ADD)
                                    }
                                    type="button"
                                    className="ms-3 me-4  button-base ing-button-primary-left align-middle"
                                >
                                    <i className="bi bi-plus-lg me-1" />
                                    {getText('msg_61cc55aa0453')}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="individuals-search-container w-100 overflow-visible">
                        <section
                            className="card-title m-4 d-flex flex-wrap mb-5 overflow-visible"
                            style={{ gap: '10px', alignItems: 'end' }}
                        >
                            <div
                                className={`form-group col-12 col-sm-12 col-md-12 col-lg-6 ${styles.indPopInput} flex-sm-wrap flex-lg-nowrap`}
                            >
                                <label htmlFor="search">
                                    {getText('msg_bce06414177f')}
                                </label>
                                <input
                                    id="search"
                                    type="text"
                                    maxLength="50"
                                    className={`form-control`}
                                    value={searchParams.search}
                                    onChange={handleInputChange}
                                    name="search"
                                    placeholder={getText('msg_ec7570c5f98c')}
                                />
                            </div>

                            <div
                                className={`form-group col-12 col-sm-12 col-md-12 col-lg-5 ${styles.indPopInput}`}
                            >
                                <label htmlFor="populationGroup">
                                    {getText('msg_eb8f3ce2a98e')}
                                </label>
                                <SearchableDropdown
                                    key={`populationGroups-${filterResetKey}`}
                                    id="populationGroup"
                                    options={populationGroups}
                                    optionIDField="id"
                                    savedOptions={searchParams.populationGroups.map(
                                        id =>
                                            populationGroups.find(
                                                group => group.id === id,
                                            ) || {
                                                id: '',
                                                name: '',
                                            },
                                    )}
                                    isLoading={isPopulationGroupLoading}
                                    savedOptionIDField="id"
                                    showSelectionBox={false}
                                    showSelectedCount={true}
                                    showSelectedItemsInField={false}
                                    updateOptions={selectedOptions =>
                                        updateSearchParamList(
                                            'populationGroups',
                                            selectedOptions,
                                        )
                                    }
                                    className={styles.searchableDropdown}
                                />
                            </div>
                        </section>
                    </div>
                    {isLoading ? (
                        <Spinner width={50} className={styles.loadingSpinner} />
                    ) : (
                        <>
                            <div className="table-responsive-xxl w-100">
                                <table className="table table-hover align-middle">
                                    <colgroup>
                                        <col className={styles.colUniqueId} />
                                        <col className={styles.colLastName} />
                                        <col className={styles.colFirstName} />
                                        <col className={styles.colStatus} />
                                        <col className={styles.colCategory} />
                                        <col className={styles.colJobTitle} />
                                        <col className={styles.colLastLogin} />
                                        {canImpersonate && (
                                            <col
                                                className={
                                                    styles.colImpersonate
                                                }
                                            />
                                        )}
                                    </colgroup>
                                    <thead className="custom-table-header table-light">
                                        <tr>
                                            <th
                                                style={{
                                                    paddingLeft: '30px',
                                                }}
                                            >
                                                <span className="table-column-header-text">
                                                    {uniqueIdLabel}
                                                </span>
                                                {renderSortArrow('uniqueId')}
                                            </th>
                                            <th>
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_863cb39fbe7d',
                                                    )}
                                                </span>
                                                {renderSortArrow('lastName')}
                                            </th>
                                            <th>
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_b6ea992aab46',
                                                    )}
                                                </span>
                                                {renderSortArrow('firstName')}
                                            </th>
                                            <th>
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_6e702cb4e0c7',
                                                    )}
                                                </span>
                                                {renderSortArrow(
                                                    'tenantUserStatus',
                                                )}
                                                {renderColumnFilter('statuses')}
                                            </th>
                                            <th>
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_a3c686e711e4',
                                                    )}
                                                </span>
                                                {renderSortArrow(
                                                    'employmentCategory',
                                                )}
                                                {renderColumnFilter(
                                                    'employmentCategories',
                                                )}
                                            </th>
                                            <th>
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_d42977925117',
                                                    )}
                                                </span>
                                                {renderSortArrow('jobTitle')}
                                                {renderColumnFilter(
                                                    'jobTitles',
                                                )}
                                            </th>
                                            <th>
                                                <span className="table-column-header-text">
                                                    {getText(
                                                        'msg_2df48ad223be',
                                                    )}
                                                </span>
                                                {renderSortArrow(
                                                    'lastLoginDate',
                                                )}
                                            </th>
                                            {canImpersonate && (
                                                <th
                                                    style={{
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    <span className="table-column-header-text">
                                                        {getText(
                                                            'msg_fa304b37de07',
                                                        )}
                                                    </span>
                                                </th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {individuals.length === 0 && (
                                            <EmptyTableRow
                                                colSpan={columnCount}
                                                title={getText(
                                                    'msg_aca14ec90fb5',
                                                )}
                                                description={getText(
                                                    'msg_5328029ad816',
                                                )}
                                            />
                                        )}
                                        {individuals.map(result => (
                                            <tr key={result.id}>
                                                <td
                                                    className={`${styles.titleCell} item-page-name`}
                                                    style={{
                                                        paddingLeft: '30px',
                                                    }}
                                                    onClick={() =>
                                                        navigate(
                                                            ROUTES.INDIVIDUALS_VIEW.replace(
                                                                ':individualId',
                                                                result.id,
                                                            ),
                                                        )
                                                    }
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={e => {
                                                        if (
                                                            e.key === 'Enter' ||
                                                            e.key === ' '
                                                        ) {
                                                            navigate(
                                                                ROUTES.INDIVIDUALS_VIEW.replace(
                                                                    ':individualId',
                                                                    result.id,
                                                                ),
                                                            );
                                                        }
                                                    }}
                                                >
                                                    {result.uniqueId}
                                                </td>
                                                <td
                                                    className={
                                                        styles.subtitleCell
                                                    }
                                                >
                                                    {result.lastName}
                                                </td>
                                                <td
                                                    className={
                                                        styles.subtitleCell
                                                    }
                                                >
                                                    {result.firstName}
                                                </td>
                                                <td>
                                                    <button
                                                        className={getStatusClass(
                                                            result.tenantUserStatusId ||
                                                                0,
                                                        )}
                                                    >
                                                        {
                                                            result.tenantUserStatus
                                                        }
                                                    </button>
                                                </td>
                                                <td
                                                    className={
                                                        styles.subtitleCell
                                                    }
                                                >
                                                    {result.employmentCategory ||
                                                        '-'}
                                                </td>
                                                <td
                                                    className={
                                                        styles.subtitleCell
                                                    }
                                                    className={
                                                        styles.subtitleCell
                                                    }
                                                >
                                                    {result.jobTitle || '-'}
                                                </td>
                                                <td
                                                    className={
                                                        styles.subtitleCell
                                                    }
                                                >
                                                    {result?.lastLoginDate !==
                                                    null
                                                        ? convertUTCDateTimeToLocalDateTime(
                                                              format(
                                                                  result.lastLoginDate,
                                                                  'M/d/yyyy, h:mm:ss a',
                                                              ),
                                                          )
                                                        : '-'}
                                                </td>
                                                {canImpersonate && (
                                                    <td
                                                        style={{
                                                            cursor: 'pointer',
                                                            textAlign: 'center',
                                                        }}
                                                        onClick={() =>
                                                            handleImpersonate(
                                                                result.tenantUserExternalId,
                                                            )
                                                        }
                                                        title={getText(
                                                            'msg_e2dc9a414e47',
                                                        )}
                                                    >
                                                        {result.tenantUserStatusId !=
                                                        TenantUserStatusEnum.Active ? (
                                                            '-'
                                                        ) : (
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="24"
                                                                height="24"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                role="img"
                                                                aria-label={getText(
                                                                    'msg_47efd22127ca',
                                                                )}
                                                            >
                                                                <circle
                                                                    cx="12"
                                                                    cy="12"
                                                                    r="12"
                                                                    fill="#007bff"
                                                                />
                                                                <path
                                                                    d="M9 15L15 9"
                                                                    stroke="white"
                                                                    strokeWidth="2"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />
                                                                <path
                                                                    d="M9 9h6v6"
                                                                    stroke="white"
                                                                    strokeWidth="2"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />
                                                            </svg>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {totalPages > 1 && (
                                <div className="mt-auto">
                                    <PaginationComponent
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default IndividualSearchPage;
